import { useCallback, useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, ReloadOutlined } from "../components/icons";
import { trpc } from "../trpc";
import {
  AI_API_PROTOCOL_LABELS,
  AI_API_PROTOCOLS,
  type AiApiProtocol,
  type AiModelItem,
} from "../../shared/aiTypes";

const { Title, Text } = Typography;

const PROTOCOL_OPTIONS = AI_API_PROTOCOLS.map((value) => ({
  value,
  label: AI_API_PROTOCOL_LABELS[value],
}));

export function AiModelPage() {
  const { message } = App.useApp();
  const [models, setModels] = useState<AiModelItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeModelId, setActiveModelId] = useState<string | null>(null);
  const [imageUnderstandingModelId, setImageUnderstandingModelId] = useState<string | null>(null);
  const [settingActiveId, setSettingActiveId] = useState<string | null>(null);
  const [settingImageUnderstandingId, setSettingImageUnderstandingId] = useState<string | null>(null);
  const [updatingProtocolId, setUpdatingProtocolId] = useState<string | null>(null);

  const loadModels = useCallback(async () => {
    setLoading(true);
    try {
      const [list, active, imageUnderstanding] = await Promise.all([
        trpc.aiModel.list.query(),
        trpc.aiModel.getGlobalActive.query(),
        trpc.aiModel.getImageUnderstandingActive.query(),
      ]);
      setModels(list);
      setActiveModelId(active?.modelId ?? null);
      setImageUnderstandingModelId(imageUnderstanding?.modelId ?? null);
    } catch {
      message.error("加载模型失败");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const handleSetGlobalActive = async (modelId: string) => {
    setSettingActiveId(modelId);
    try {
      await trpc.aiModel.setGlobalActive.mutate({ modelId });
      setActiveModelId(modelId);
      message.success("已设为全局使用中");
      loadModels();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "设置失败");
    } finally {
      setSettingActiveId(null);
    }
  };

  const handleSetImageUnderstandingActive = async (modelId: string) => {
    setSettingImageUnderstandingId(modelId);
    try {
      await trpc.aiModel.setImageUnderstandingActive.mutate({ modelId });
      setImageUnderstandingModelId(modelId);
      message.success("已设为图片理解使用中");
      loadModels();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "设置失败");
    } finally {
      setSettingImageUnderstandingId(null);
    }
  };

  const handleToggleEnabled = async (record: AiModelItem, enabled: boolean) => {
    try {
      await trpc.aiModel.updateEnabled.mutate({ modelId: record.modelId, enabled });
      message.success(enabled ? "模型已启用" : "模型已禁用");
      loadModels();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "更新状态失败");
    }
  };

  const handleUpdateProtocol = async (record: AiModelItem, protocol: AiApiProtocol) => {
    if (record.protocol === protocol) return;
    setUpdatingProtocolId(record.modelId);
    try {
      await trpc.aiModel.updateProtocol.mutate({ modelId: record.modelId, protocol });
      setModels((prev) =>
        prev.map((item) =>
          item.modelId === record.modelId ? { ...item, protocol } : item
        )
      );
      message.success(`已切换为 ${AI_API_PROTOCOL_LABELS[protocol]}`);
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "更新协议失败");
    } finally {
      setUpdatingProtocolId(null);
    }
  };

  const handleDelete = async (record: AiModelItem) => {
    try {
      await trpc.aiModel.delete.mutate({ modelId: record.modelId });
      message.success("删除成功");
      loadModels();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "删除失败");
    }
  };

  const activeModel = models.find((item) => item.modelId === activeModelId);
  const imageUnderstandingModel = models.find(
    (item) => item.modelId === imageUnderstandingModelId
  );

  const columns: ColumnsType<AiModelItem> = [
    {
      title: "全局使用中",
      key: "isGlobalActive",
      width: 110,
      render: (_, record) => (
        <Radio
          checked={record.modelId === activeModelId}
          disabled={!record.enabled || settingActiveId === record.modelId}
          onChange={() => handleSetGlobalActive(record.modelId)}
        />
      ),
    },
    {
      title: "图片理解",
      key: "isImageUnderstandingActive",
      width: 110,
      render: (_, record) => (
        <Radio
          checked={record.modelId === imageUnderstandingModelId}
          disabled={!record.enabled || settingImageUnderstandingId === record.modelId}
          onChange={() => handleSetImageUnderstandingActive(record.modelId)}
        />
      ),
    },
    {
      title: "供应商",
      dataIndex: "providerName",
      key: "providerName",
      width: 140,
    },
    {
      title: "模型 ID",
      dataIndex: "remoteId",
      key: "remoteId",
      ellipsis: true,
    },
    {
      title: "显示名称",
      dataIndex: "displayName",
      key: "displayName",
      width: 180,
      ellipsis: true,
    },
    {
      title: "协议类型",
      dataIndex: "protocol",
      key: "protocol",
      width: 180,
      render: (protocol: AiApiProtocol, record) => (
        <Select
          size="small"
          style={{ width: 160 }}
          value={protocol}
          options={PROTOCOL_OPTIONS}
          loading={updatingProtocolId === record.modelId}
          disabled={updatingProtocolId === record.modelId}
          onChange={(value: AiApiProtocol) => handleUpdateProtocol(record, value)}
        />
      ),
    },
    {
      title: "启用",
      dataIndex: "enabled",
      key: "enabled",
      width: 90,
      render: (enabled: boolean, record) => (
        <Switch
          checked={enabled}
          disabled={record.isGlobalActive || record.isImageUnderstandingActive}
          onChange={(checked) => handleToggleEnabled(record, checked)}
        />
      ),
    },
    {
      title: "状态",
      key: "status",
      width: 180,
      render: (_, record) => (
        <Space size={4} wrap>
          {record.isGlobalActive ? <Tag color="gold">全局使用中</Tag> : null}
          {record.isImageUnderstandingActive ? <Tag color="blue">图片理解</Tag> : null}
          {!record.isGlobalActive && !record.isImageUnderstandingActive ? (
            record.enabled ? <Tag color="success">可用</Tag> : <Tag>已禁用</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: "操作",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Popconfirm
          title="删除模型"
          description={`确定要删除 "${record.remoteId}" 吗？`}
          onConfirm={() => handleDelete(record)}
          okText="确定"
          cancelText="取消"
          okButtonProps={{ danger: true }}
          disabled={record.isGlobalActive || record.isImageUnderstandingActive}
        >
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            danger
            disabled={record.isGlobalActive || record.isImageUnderstandingActive}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <Card
        variant="borderless"
        className="shadow-sm"
        title={
          <Title level={5} style={{ margin: 0 }}>
            AI 模型管理
          </Title>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={loadModels} loading={loading}>
            刷新
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">全局使用中：</Text>{" "}
          {activeModel ? (
            <Tag color="gold">
              {activeModel.providerName} / {activeModel.remoteId}
            </Tag>
          ) : (
            <Tag color="warning">未设置</Tag>
          )}
          <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
            图片理解使用中：
          </Text>{" "}
          {imageUnderstandingModel ? (
            <Tag color="blue">
              {imageUnderstandingModel.providerName} / {imageUnderstandingModel.remoteId}
            </Tag>
          ) : (
            <Tag color="warning">未设置</Tag>
          )}
          <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            系统业务将使用全局模型；飞书剧本图片理解使用独立的视觉模型（建议 GPT-4o 系列）。
            仅支持 Responses 协议的模型（如 gpt-5.4-mini）请将协议类型切换为 Responses。
          </Text>
        </div>

        <Table
          columns={columns}
          dataSource={models}
          rowKey="modelId"
          loading={loading}
          scroll={{ x: 1280 }}
          locale={{
            emptyText: (
              <Spin spinning={loading}>
                <div style={{ padding: "40px 0" }}>暂无模型，请先在供应商管理中导入</div>
              </Spin>
            ),
          }}
        />
      </Card>
    </div>
  );
}
